import React, { useState, useEffect, useRef } from 'react';
import { ChatMessage } from './ChatMessage';
import { ContactForm } from './ContactForm';
import { HelpChatbotService } from './chatbotService';
import { 
  ChatMessage as ChatMessageType, 
  ChatbotState, 
  ContactFormData,
  SuggestedQuestion 
} from './types';
import './ChatWidget.css';

export const ChatWidget: React.FC = () => {
  const [state, setState] = useState<ChatbotState>({
    isOpen: false,
    messages: [],
    isLoading: false,
    error: null,
    showContactForm: false
  });

  const [inputValue, setInputValue] = useState('');
  const [suggestedQuestions, setSuggestedQuestions] = useState<SuggestedQuestion[]>([]);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load suggested questions on mount
  useEffect(() => {
    loadSuggestedQuestions();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [state.messages, state.isLoading]);

  // Focus input when widget opens
  useEffect(() => {
    if (state.isOpen && inputRef.current && !state.showContactForm) {
      inputRef.current.focus();
    }
  }, [state.isOpen, state.showContactForm]);

  const loadSuggestedQuestions = async () => {
    try {
      const response = await HelpChatbotService.getSuggestedQuestions();
      if (response.status === 'success') {
        setSuggestedQuestions(response.result.questions);
      }
    } catch (error) {
      console.error('Failed to load suggested questions:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleWidget = () => {
    setState(prev => ({ 
      ...prev, 
      isOpen: !prev.isOpen,
      showContactForm: false 
    }));
  };

  const addMessage = (role: 'user' | 'assistant', content: string, extras?: Partial<ChatMessageType>) => {
    const newMessage: ChatMessageType = {
      id: Date.now().toString(),
      role,
      content,
      timestamp: new Date(),
      ...extras
    };

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, newMessage]
    }));
  };

  const handleSendMessage = async (query?: string) => {
    const messageToSend = query || inputValue.trim();
    
    if (!messageToSend) return;

    // Add user message
    addMessage('user', messageToSend);
    setInputValue('');
    setState(prev => ({ ...prev, isLoading: true, error: null, showContactForm: false }));

    try {
      const response = await HelpChatbotService.sendMessage(messageToSend);

      if (response.status === 'success') {
        // Add assistant response
        addMessage('assistant', response.result.answer, {
          sources: response.result.sources,
          metadata: response.result.metadata
        });

        // Check if the answer indicates uncertainty or lack of information
        const lowConfidenceKeywords = [
          'i apologize',
          'i could not find',
          'not in the documentation',
          'please contact support',
          'unable to find',
          'no information'
        ];

        const isLowConfidence = lowConfidenceKeywords.some(keyword => 
          response.result.answer.toLowerCase().includes(keyword)
        );

        if (isLowConfidence) {
          // Show contact form after a delay
          setTimeout(() => {
            setState(prev => ({ ...prev, showContactForm: true }));
          }, 1000);
        }
      } else {
        addMessage('assistant', 
          response.result?.answer || response.message || 'I apologize, but I encountered an error. Please try again or contact our support team.'
        );
        setState(prev => ({ ...prev, showContactForm: true }));
      }
    } catch (error: any) {
      setState(prev => ({ ...prev, error: error.message }));
      addMessage('assistant', 
        'I apologize, but I\'m having trouble connecting right now. Please try again in a moment or contact our support team.'
      );
      setState(prev => ({ ...prev, showContactForm: true }));
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    handleSendMessage(question);
  };

  const handleContactFormSubmit = async (data: ContactFormData) => {
    setIsSubmittingContact(true);
    
    try {
      await HelpChatbotService.submitContactForm(data);
      
      // Show success message
      addMessage('assistant', 
        `✅ Thank you, ${data.name}! We've received your request and will contact you soon at ${data.email} or ${data.phone}.`
      );
      
      setState(prev => ({ ...prev, showContactForm: false }));
    } catch (error: any) {
      setState(prev => ({ ...prev, error: error.message }));
      addMessage('assistant', 
        'Sorry, there was an error submitting your request. Please try again or email us directly at support@wiziai.com'
      );
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleContactFormCancel = () => {
    setState(prev => ({ ...prev, showContactForm: false }));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        className={`chat-widget-button ${state.isOpen ? 'open' : ''}`}
        onClick={toggleWidget}
        aria-label="Help Chatbot"
      >
        {state.isOpen ? '✕' : '💬'}
        {!state.isOpen && state.messages.length === 0 && (
          <span className="chat-button-badge">Help</span>
        )}
      </button>

      {/* Chat Window */}
      {state.isOpen && (
        <div className="chat-widget-window">
          {/* Header */}
          <div className="chat-widget-header">
            <div className="header-content">
              <div className="header-icon">🤖</div>
              <div className="header-text">
                <h3>WiziAI Help Assistant</h3>
                <p>Ask me anything about WiziAI</p>
              </div>
            </div>
            <button onClick={toggleWidget} className="header-close" aria-label="Close">
              ✕
            </button>
          </div>

          {/* Messages Container */}
          <div className="chat-widget-messages">
            {state.messages.length === 0 && (
              <div className="chat-welcome">
                <div className="welcome-icon">👋</div>
                <h4>Welcome to WiziAI Help!</h4>
                <p>I'm here to answer your questions about our platform, features, and more.</p>

                {/* Suggested Questions */}
                {suggestedQuestions.length > 0 && (
                  <div className="suggested-questions">
                    <h5>Popular Questions:</h5>
                    {suggestedQuestions.slice(0, 2).map((category, idx) => (
                      <div key={idx} className="question-category">
                        <p className="category-title">{category.category}</p>
                        {category.questions.slice(0, 3).map((question, qIdx) => (
                          <button
                            key={qIdx}
                            className="suggested-question-btn"
                            onClick={() => handleSuggestedQuestion(question)}
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {state.messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}

            {state.isLoading && (
              <div className="typing-indicator">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="typing-text">AI is thinking...</span>
              </div>
            )}

            {state.showContactForm && (
              <ContactForm
                query={state.messages[state.messages.length - 2]?.content || ''}
                onSubmit={handleContactFormSubmit}
                onCancel={handleContactFormCancel}
                isSubmitting={isSubmittingContact}
              />
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          {!state.showContactForm && (
            <div className="chat-widget-input">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question here..."
                disabled={state.isLoading}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || state.isLoading}
                className="send-button"
                aria-label="Send message"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="chat-widget-footer">
            <span>Powered by AI • WiziAI © 2025</span>
          </div>
        </div>
      )}
    </>
  );
};
